<template>
  <a-spin :spinning="pending">
    <div class="w-full h-full flex gap-x-[10px]">
      <div class="flex-1 justify-between rounded-[12px] bg-back-1 flex">
        <div v-if="paySucc" class="pay-success w-full">
          <PaySucc
            :title="paySuccTitle"
            :desc="paySuccDesc"
            :invite-props="{
              isInviteUser,
              award: inviterAward,
              cb: goInvite,
            }"
          >
          </PaySucc>
          <div class="bar w-[92%] mx-auto h-[1px] bg-back-3"></div>
          <PaySuccTip class="my-[28px]" :url="getQrcodeURI('c', '3')" />
        </div>
        <div v-else class="flex flex-1 flex-col p-[24px] gap-[28px]">
          <XAlert v-if="needCreditTip" type="warning">
            {{
              $t(
                'tao-can-sheng-xiao-zhong-xu-yue-hou-quan-yi-jiang-yu-rightsviewgetexpiredata-sheng-xiao-ru-guo-xu-yao-zeng-gou-yan-bi-qing-dan-du-gou-mai',
                [expiredDateRange]
              )
            }}
            <span class="text-[var(--warning-8)] cursor-pointer mr-[3px]" @click="onAddCreditCoins">
              {{ $t('yan-bi-jia-gou-bao') }}
            </span>
            <template v-if="rights?.length > 0">
              {{ $t('huo') }}
              <span class="text-[var(--warning-8)] cursor-pointer" @click="onUpgradePackage">
                {{ $t('sheng-ji-tao-can') }}
              </span>
            </template>
          </XAlert>

          <div class="auth-type">
            <div class="auth-type-title flex items-center">
              {{ $t('ge-ren-shang-yong-shou-quan') }}
              <authorization-differences v-slot="{ show }" :onCloseParent="onCloseParent">
                <iconpark-icon
                  name="info-fill"
                  size="16"
                  class="mt-[4px] cursor-pointer text-[var(--text-1)] ml-[4px]"
                  @click="show"
                />
              </authorization-differences>
            </div>
            <div class="auth-type-desc">
              {{ $t('jin-xian-ge-ren-shi-yong-bu-ke-yong-yu-qi-ye-shang-shi-huo-dong') }}
            </div>
            <div class="buy-enterprise-type" @click="onSubscribe">
              <span class="mr-[4px] font-[600]">
                {{ $t('wo-yao-gou-mai-qi-ye-shang-yong-tao-can') }}
              </span>
              <iconpark-icon name="arrowright" size="16"></iconpark-icon>
            </div>
          </div>
          <div class="fees-list flex flex-col mt-[0px]">
            <div class="text-[14px] text-font-1">
              <span class="font-bold">
                {{ personalRights.rights.value?.rights_name }}
              </span>
              <nuxt-link
                class="ml-[8px] inline-flex justify-center items-center standard"
                :to="{ name: PageKey.SUBSCRIBE }"
                @click="onCloseParent"
              >
                {{ $t('cha-kan-wan-zheng-quan-yi-dui-bi') }}
                <iconpark-icon name="arrowright"></iconpark-icon>
              </nuxt-link>
            </div>
            <div class="fee-grid">
              <Fees
                v-for="f in personalRights.rights?.value?.rights_by_time"
                :key="f.id"
                :fees="f"
                :selected="f.id === currentFee?.id"
                :discount="discount1"
                @on-select="f => onSelectFees(f, USER_LEVEL_KEY.PERSONAL)"
              />
            </div>
            <div class="text-[14px] text-font-1 mt-[24px]">
              <span class="font-bold">
                {{ personalLiteRights.rights.value?.rights_name }}
              </span>
            </div>
            <div class="fee-grid">
              <Fees
                v-for="f in personalLiteRights.rights?.value?.rights_by_time"
                :key="f.id"
                :fees="f"
                :selected="f.id === currentFee?.id"
                :discount="discount2"
                @on-select="f => onSelectFees(f, USER_LEVEL_KEY.PERSONAL_LITE)"
              />
            </div>
          </div>
        </div>
        <div v-if="!paySucc" class="w-[1px] bg-[#f1f1f1]"></div>
        <div v-if="order.status === undefined" class="flex">
          <PayQrcode
            v-model:payType="payType"
            v-model:agreementChecked="agreementChecked"
            :buyType="currentFee?.buy_type"
            :loading="creating"
            :qrcode="qrcode"
            :reorder="createOrder"
            :behavior="behavior"
            :priceText="currentFee?.price || 0"
            :expiredDate="expiredDateRange"
          >
            <Consultant
              class="mt-[20px]"
              :rightType="currentRightType"
              @transfer="onTransferClick"
            />
          </PayQrcode>
        </div>
        <PayFail v-else-if="!paySucc" @back="onRePay" />
      </div>
    </div>
  </a-spin>
</template>

<script setup lang="ts">
import useOrder, { useRights } from '../../../useOrder';
import useCheckUserInvited from '../../../useCheckUserInvited';
import PayQrcode from '../../pay-qrcode.vue';
import Consultant from '../../consultant.vue';
import PaySucc from '../../pay-succ.vue';
import PaySuccTip from '../../pay-succ-tip.vue';
import PayFail from '../../pay-fail.vue';
import AuthorizationDifferences from '../authorization-differences.vue';
import XAlert from '@/components/xAlert/index.vue';
import { getQrcodeURI } from '~/utils/url';
import { BUY_TYPE, RIGHTS_FEATURE, type IFees } from '~/api/interface/rights';
import Fees from '~/pages/center/equity/components/fees.vue';
import { USER_LEVEL_KEY } from '~/api/interface/auth';
import { ORDER_BEHAVIOR, ORDER_TYPE } from '~/api/interface';
import { useAddFeature, useUpgradeEquity } from '~/features/paymentDesk';
import { actionUpdateCycle } from '~/composables/rights';
import { formatPrice } from '~/utils/equity-helper';
import { PageKey } from '~/constants/page';
import { useTime } from '~/hooks/useTime';
import { useUpgradePackage } from '~/hooks/useUpgradePackage';

const props = defineProps<{
  behavior: ORDER_BEHAVIOR;
  order_type: ORDER_TYPE;
  rightsType: USER_LEVEL_KEY;
  onCloseParent: () => void;
}>();

const userInfo = useUserInfo();

const { rights } = useUpgradePackage();

const needCreditTip = computed(() =>
  [USER_LEVEL_KEY.PERSONAL, USER_LEVEL_KEY.PERSONAL_LITE].includes(userInfo.value!.rights_type)
);

const personalLiteRights = useRights(USER_LEVEL_KEY.PERSONAL_LITE, fees => {
  const fee = fees.find(v => v.days === 365)!;
  return fee;
});

const personalRights = useRights(USER_LEVEL_KEY.PERSONAL, fees => {
  const fee = fees.find(v => v.days === 365)!;
  return fee;
});

const currentFee = ref<IFees>();
const currentRightType = ref(props.rightsType);

function onSelectFees(fees: IFees, rightType: USER_LEVEL_KEY) {
  currentFee.value = fees;
  currentRightType.value = rightType;
}

const { isInviteUser, inviterAward, getInviteInfo } = useCheckUserInvited(
  personalRights.currentFees
);

const yuanFormat = computed(() => formatPrice(currentFee.value!.price).string);

const discount1 = computed(() => {
  const costByMonth = personalRights.rights.value?.rights_by_time.find(
    v => v.days === 30 && v.buy_type === 0
  );
  const costByYear = personalRights.rights.value?.rights_by_time.find(
    v => v.days === 365 && v.buy_type === 0
  );
  if (costByMonth && costByYear) {
    return formatPrice(costByMonth?.price * 12 - costByYear.price).string;
  }

  return '';
});

const discount2 = computed(() => {
  const costByMonth = personalLiteRights.rights.value?.rights_by_time.find(
    v => v.days === 30 && v.buy_type === 0
  );
  const costByYear = personalLiteRights.rights.value?.rights_by_time.find(
    v => v.days === 365 && v.buy_type === 0
  );
  if (costByMonth && costByYear) {
    return formatPrice(costByMonth?.price * 12 - costByYear.price).string;
  }

  return '';
});

const data = computed(() => ({
  buy_type: currentFee.value?.buy_type,
  behavior: props.behavior,
  rights_type: currentRightType.value,
  rights_id: currentFee.value?.id,
  order_type: props.order_type,
  time_identity: currentFee.value?.time_identity,
  parent_order_id: userInfo.value!.current_order_id,
}));

const { $t } = useNuxtApp();

const { agreementChecked, payType, qrcode, order, paySucc, onRePay, creating, createOrder } =
  useOrder(data);

const onTransferClick = () => {
  createOrder(true);
};

const paySuccTitle = computed(() => yuanFormat.value);

const paySuccDesc = computed(() => {
  const payAction = (() => {
    if (currentFee.value?.buy_type === BUY_TYPE.PAYMENT) {
      return currentFee.value.days === 30 ? $t('dan-yue') : $t('dan-nian');
    }
    return currentFee.value?.days === 30 ? $t('lian-xu-bao-yue') : $t('lian-xu-bao-nian');
  })();

  switch (currentRightType.value) {
    case USER_LEVEL_KEY.PERSONAL_LITE:
      return `${personalLiteRights.rights.value?.rights_name} ${payAction}`;
    case USER_LEVEL_KEY.PERSONAL:
      return `${personalRights.rights.value?.rights_name} ${payAction}`;
    default:
      return '';
  }
});

const { $dayjs } = useTime();

const expiredDateRange = computed(() => {
  let endDate = $dayjs()
    .add(currentFee.value?.days || 0, 'day')
    .format('YYYY.MM.DD');

  let beginDate = $dayjs().format('YYYY.MM.DD');

  if (userInfo.value!.personal_expired_time) {
    beginDate = $dayjs(userInfo.value!.personal_expired_time).format('YYYY.MM.DD');
    endDate = $dayjs(userInfo.value!.personal_expired_time)
      .add(currentFee.value?.days || 0, 'day')
      .format('YYYY.MM.DD');
  }
  return beginDate + '~' + endDate;
});

function goInvite() {
  const router = useRouter();
  router.push({ name: PageKey.PULL_NEW });
}

function onUpgradePackage() {
  useUpgradeEquity({});
}

function onAddCreditCoins() {
  if (userInfo.value!.is_expired) {
    return;
  }

  useAddFeature({
    title: $t('jia-gou-yan-bi'),
    feature_type: RIGHTS_FEATURE.credit_coin,
    order_type: ORDER_TYPE.CREDITS,
    user_info: userInfo.value,
    behavior: ORDER_BEHAVIOR.ADD_PURCHASE,
  });
}

function onSubscribe() {
  actionUpdateCycle('team');
  props.onCloseParent();
  navigateTo({ name: PageKey.SUBSCRIBE });
}

// 购买成功后首次
watch(
  () => paySucc.value,
  (cur, before) => {
    if (cur !== before && cur) {
      if (isInviteUser.value) {
        getInviteInfo(true);
        useUpdateUser();
      }
    }
  }
);

const pending = ref(false);

onMounted(async () => {
  pending.value = true;
  await Promise.all([personalLiteRights.getRights(), personalRights.getRights()]);
  pending.value = false;

  switch (props.rightsType) {
    case USER_LEVEL_KEY.PERSONAL_LITE:
      currentFee.value = personalLiteRights.rights.value?.rights_by_time.find(
        v => v.buy_type === BUY_TYPE.SUBSCRIBE && v.days === 30
      );
      break;
    case USER_LEVEL_KEY.PERSONAL:
      currentFee.value = personalRights.rights.value?.rights_by_time.find(
        v => v.buy_type === BUY_TYPE.SUBSCRIBE && v.days === 30
      );
      break;
  }

  getInviteInfo();
});
</script>

<style lang="scss">
.fees-list {
  display: flex;
  &:not(:last-child) {
    margin-bottom: 24px;
  }
  .fee-grid {
    margin-top: 12px;
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 12px;
  }
}
</style>
<style lang="scss" scoped>
.auth-type {
  position: relative;
  width: 100%;
  padding: 0 0px 0px 10px;
  .auth-type-title {
    color: var(--warning-7);
    font-weight: 600;
    line-height: 20px;
  }
  .auth-type-desc {
    margin-top: 2px;
    color: var(--text-1);
    font-size: 12px;
    line-height: 20px;
  }
  .buy-enterprise-type {
    position: absolute;
    cursor: pointer;
    right: 0px;
    top: 50%;
    transform: translateY(-50%);
    height: 44px;
    border-radius: 8px;
    color: var(--warning-10);
    background-color: var(--warning-3);
    padding: 12px 16px;
    display: flex;
    justify-content: center;
    align-items: center;
  }
}
</style>
